import React from 'react';
import { Target, Download } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LeadSourceTrend } from './LeadSourceTrend';

const SRC_BAR = { google: 'bg-amber-500', meta: 'bg-blue-600', other: 'bg-gray-300' };
const rateTxt = (r) => (r === null || r === undefined ? '—' : `${r}%`);

function Cell({ c, src, testid }) {
  if (!c.total) return <td className="px-2 py-2 text-center text-gray-300" data-testid={testid}>—</td>;
  return (
    <td className="px-2 py-2 text-center" data-testid={testid}>
      <div className="tabular-nums text-gray-900"><span className="font-bold">{c.booked}</span><span className="text-gray-400"> / {c.total}</span></div>
      <div className="flex items-center gap-1.5 mt-1 justify-center">
        <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className={`h-full ${SRC_BAR[src]}`} style={{ width: `${Math.max(2, c.rate || 0)}%` }} /></div>
        <span className="text-[11px] tabular-nums text-gray-600 w-10 text-left">{rateTxt(c.rate)}</span>
      </div>
    </td>
  );
}

function toCsv(data) {
  const head = ['Sector', ...data.sources.flatMap((s) => [`${s.label} enquiries`, `${s.label} booked`, `${s.label} booking rate %`]), 'Total enquiries', 'Total booked', 'Booking rate %'];
  const line = (label, srcs, t) => [label, ...data.sources.flatMap((s) => [srcs[s.key].total, srcs[s.key].booked, srcs[s.key].rate ?? '']), t.total, t.booked, t.rate ?? ''];
  const rows = [head, ...data.rows.map((r) => line(r.label, r.sources, r)), line('All sectors', data.totals.sources, data.totals)];
  return rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
}

export function LeadSourceSection({ data, start, end }) {
  if (!data) return null;
  const empty = !data.totals.total;
  const download = () => {
    const blob = new Blob([toCsv(data)], { type: 'text/csv;charset=utf-8' });
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `lead-sources-${start}-to-${end}.csv` });
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <Card className="p-5" data-testid="lead-source-section">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Target className="w-5 h-5 text-amber-500" />
        <h2 className="font-bold text-gray-900">Lead source report</h2>
        <span className="text-xs text-gray-500">Training enquiries by lead date · booked / enquiries · booking rate</span>
        <Button variant="outline" size="sm" className="ml-auto h-8" onClick={download} disabled={empty} data-testid="lead-source-csv">
          <Download className="w-3.5 h-3.5 mr-1" /> CSV
        </Button>
      </div>
      {empty ? <p className="text-sm text-gray-500 italic" data-testid="lead-source-empty">No training enquiries in this window.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="lead-source-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b">
                <th className="text-left px-2 py-2 font-semibold">Sector</th>
                {data.sources.map((s) => <th key={s.key} className="px-2 py-2 font-semibold text-center">{s.label}</th>)}
                <th className="px-2 py-2 font-semibold text-center">All sources</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.sector} className="border-b last:border-0" data-testid={`lead-source-row-${r.sector}`}>
                  <td className={`px-2 py-2 font-semibold ${r.sector === 'unknown' ? 'text-gray-500 italic' : 'text-gray-900'}`}>{r.label}</td>
                  {data.sources.map((s) => <Cell key={s.key} c={r.sources[s.key]} src={s.key} testid={`lead-source-cell-${r.sector}-${s.key}`} />)}
                  <td className="px-2 py-2 text-center tabular-nums" data-testid={`lead-source-total-${r.sector}`}>
                    <span className="font-bold text-gray-900">{r.booked}</span><span className="text-gray-400"> / {r.total}</span>
                    <div className="text-[11px] text-gray-600 mt-1">{rateTxt(r.rate)}</div>
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold" data-testid="lead-source-totals">
                <td className="px-2 py-2 text-gray-900">All sectors</td>
                {data.sources.map((s) => {
                  const c = data.totals.sources[s.key];
                  return (
                    <td key={s.key} className="px-2 py-2 text-center tabular-nums" data-testid={`lead-source-totals-${s.key}`}>
                      {c.total ? <><span className="text-gray-900">{c.booked}</span><span className="text-gray-400"> / {c.total}</span><div className="text-[11px] text-gray-600 mt-1">{rateTxt(c.rate)}</div></> : <span className="text-gray-300">—</span>}
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-center tabular-nums" data-testid="lead-source-totals-all">
                  <span className="text-gray-900">{data.totals.booked}</span><span className="text-gray-400"> / {data.totals.total}</span>
                  <div className="text-[11px] text-gray-600 mt-1">{rateTxt(data.totals.rate)}</div>
                </td>
              </tr>
            </tbody>
          </table>
          <p className="text-[11px] text-gray-500 mt-2">Meta = Facebook + Instagram. Booked = enquiry status Booked (linked to a course).</p>
          <LeadSourceTrend months={data.months} />
        </div>
      )}
    </Card>
  );
}
